// @flow
import $ from 'jquery'
import _ from 'lodash'
import React, { Component } from 'react'
import Form from './Statistics/Form'
import Table from './Statistics/Table'
import { translations, translateFunc } from '../shared/localize'
import styled from 'styled-components'
import type { OptionType } from './types'
import { groupHeaders } from './controls/groupHeaders'
import getJson from '../services/getJson'
import type { GroupedHeaders } from './controls/groupHeaders'

const StatisticsFilterFormWarning = styled.div`
  color: #444;
  font-style: italic;
  padding-top: 40px;
  padding-bottom: 20px;
`

export type AudienceOptions = {|
  id: any,
  name: string,
  campaign_id: number
|}

type Filters = {|
  date_from: string,
  date_to: string,
  campaigns_for_select: Array<OptionType>,
  audience_options_for_select: Array<AudienceOptions>,
  selected_providers: Array<string>,
  providers: Array<string>,
  total: boolean,
  group_by: string,
  group_by_option_keys: Array<string>,
  onChange?: Function,
  campaign_ids?: Array<number>,
  audience_ids?: Array<number>,
  selected_statistic_fields: Array<string>,
  statistic_fields: Array<string>
|}

type StatisticsData = {|
  filters: Filters,
  tables: Array<Object>,
  references: Object
|}

type Props = {|
  formProperties: Object,
  data: StatisticsData
|}

type State = {|
  data: StatisticsData,
  isReportLoading: boolean,
  campaignId: number,
  campaignOptions: Array<OptionType>,
  audienceId: number,
  audienceOptions: Array<AudienceOptions>,
  total: boolean,
  groupByOptions: Array<OptionType>,
  groupByValue: string,
  providers: Array<string>,
  selectedProviders: Array<string>,
  datepickerValue: string,
  appliedFormData: Object,
  statisticFields: GroupedHeaders,
  selectedStatisticFields: Array<string>
|}

const statisticsUrl = '/statistics'
const jsonStatisticsUrl = '/statistics'
const xlsxStatisticsUrl = '/statistics.xlsx'

const t = translateFunc(translations.statistics)

export default class Statistics extends Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = this.filtersToState()
    this.state.appliedFormData = this.formData()
  }

  render() {
    const { formProperties } = this.props
    const {
      isReportLoading,
      campaignId,
      audienceId,
      total,
      groupByValue,
      selectedProviders,
      datepickerValue,
      providers,
      data,
      statisticFields,
      selectedStatisticFields
    } = this.state
    const { tables, references } = data
    return (
      <div>
        <Form
          {...formProperties}
          disabled={isReportLoading}
          campaignId={campaignId}
          onCampaignIdChange={(id) => this.campaignIdChanged(id)}
          campaignOptions={this.campaignOptions()}
          audienceId={audienceId}
          onAudienceIdChange={(id) => this.audienceIdChanged(id)}
          audienceOptions={this.audienceOptions()}
          total={total}
          onTotalChange={(total) => this.totalChanged(total)}
          groupByOptions={this.groupByOptions()}
          groupByValue={groupByValue}
          onGroupByChange={(val) => this.groupByChanged(val)}
          selectedProviders={selectedProviders}
          onSelectedProvidersChange={(providers) => this.selectedProviderChanged(providers)}
          datepickerValue={datepickerValue}
          onDatepickerChange={(val) => this.datepickerChanged(val)}
          applyDisabled={!this.filtersAreModified()}
          onApply={() => this.apply()}
          providers={providers}
          statisticFields={statisticFields}
          selectedStatisticFields={selectedStatisticFields}
          onSelectedStatisticFieldsChanged={(fields) => this.selectedStatisticFieldsChanged(fields)}
          downloadXlsxUrl={this.downloadXlsxUrl()}
        />
        {this.filtersAreModified() && (
          <StatisticsFilterFormWarning>
            {isReportLoading ? t('loading') : t('filters_are_modified', { button: t('apply') })}
          </StatisticsFilterFormWarning>
        )}
        <div style={this.reportsStyle()}>
          {_.map(tables, (table, index: number) => (
            <Table key={index} data={table} references={references} />
          ))}
        </div>
      </div>
    )
  }

  reportsStyle() {
    return this.filtersAreModified() ? { opacity: 0.5 } : {}
  }

  filtersToState(): State {
    const { filters } = this.props.data
    return {
      campaignId: filters.campaign_ids ? parseInt(filters.campaign_ids) : -1,
      campaignOptions: filters.campaigns_for_select,
      audienceId: filters.audience_ids ? parseInt(filters.audience_ids) : -1,
      audienceOptions: filters.audience_options_for_select,
      total: filters.total,
      groupByOptions: this.filtersGroupByOptions(),
      groupByValue: filters.group_by,
      providers: filters.providers,
      selectedProviders: filters.selected_providers,
      datepickerValue: filters.date_from + ' - ' + filters.date_to,
      selectedStatisticFields: filters.selected_statistic_fields,
      statisticFields: groupHeaders(filters.statistic_fields),
      appliedFormData: {},
      data: this.props.data,
      isReportLoading: false
    }
  }

  filtersGroupByOptions(): Array<OptionType> {
    const { filters } = this.props.data
    return _.map(filters.group_by_option_keys, (key) => ({
      id: key,
      name: t('group_by_value.' + key)
    }))
  }

  filtersAreModified() {
    return !_.isEqual(this.state.appliedFormData, this.formData())
  }

  campaignIdChanged(id: number) {
    const audiences = this.props.data.filters.audience_options_for_select

    if (id === -1) {
      this.setState({ campaignId: id, audienceOptions: audiences, audienceId: -1 })
    } else {
      const filteredAudiences = _.filter(audiences, ['campaign_id', id])
      this.setState({ campaignId: id, audienceId: -1, audienceOptions: filteredAudiences })
    }
  }

  audienceIdChanged(id: number) {
    this.setState({ audienceId: id })
  }

  commonOption(): Array<OptionType> {
    return [{ name: t('all'), id: -1 }]
  }

  withId(option: OptionType) {
    return { id: option.id, name: '[#' + option.id + '] ' + option.name }
  }

  campaignOptions(): Array<OptionType> {
    return _.concat(
      this.commonOption(),
      _.map(this.state.campaignOptions, (option: OptionType) => this.withId(option))
    )
  }

  totalChanged(total: boolean) {
    this.setState({ total })
  }

  selectedProviderChanged(providers: Array<string>) {
    this.setState({ selectedProviders: providers })
  }

  datepickerChanged(datepickerValue: string) {
    this.setState({ datepickerValue })
  }

  audienceOptions(): Array<OptionType> {
    return _.concat(
      this.commonOption(),
      _.map(this.state.audienceOptions, (option: any) => this.withId(option))
    )
  }

  groupByChanged(groupByValue: string) {
    this.setState({ groupByValue })
  }

  groupByOptions(): Array<OptionType> {
    let additionalOptions = this.additionalOptions()
    return this.state.groupByOptions.concat(additionalOptions)
  }

  additionalOptions(): Array<OptionType> {
    let audienceOption = { id: 'audience_id', name: t('group_by_value.audiences') }
    let advertisementOption = { id: 'advertisement_id', name: t('group_by_value.advertisements') }
    if (this.state.audienceId === -1) {
      return [audienceOption, advertisementOption]
    } else {
      return [advertisementOption]
    }
  }

  apply() {
    let formData = this.formData()
    this.replaceHistoryState(formData)

    this.setState({ isReportLoading: true }, () => {
      getJson(jsonStatisticsUrl, _.extend({}, formData, { rand: Math.random() }), { async: true })
        .done((response) => this.setState({ data: response, appliedFormData: formData }))
        .fail(() => window.alert('Internal Server error'))
        .always(() => this.setState({ isReportLoading: false }))
    })
  }

  replaceHistoryState(formData: Object) {
    if (!window.history.replaceState) return

    window.history.replaceState(formData, '', statisticsUrl + '?' + $.param(this.formData()))
  }

  downloadXlsxUrl() {
    return xlsxStatisticsUrl + '?' + $.param(this.formData())
  }

  formData() {
    let state = this.state

    return {
      filter_form: {
        campaign_ids: state.campaignId === -1 ? '' : state.campaignId.toString(),
        audience_ids: state.audienceId === -1 ? '' : state.audienceId.toString(),
        providers: state.selectedProviders,
        total: state.total,
        group_by: state.groupByValue,
        datepicker: state.datepickerValue,
        statistic_fields: state.selectedStatisticFields
      }
    }
  }

  selectedStatisticFieldsChanged(fields: Array<string>) {
    this.setState({ selectedStatisticFields: fields })
  }
}
